// useUnreads — read-path hook for the All Unreads activity view.
//
// The digest lives in `MatrixContext.unreadGroups` (assembled at the
// subscription seam). This hook is a thin projection: it surfaces the
// groups, an aggregate badge count for the sidebar, and the
// mark-all action directly so the view doesn't reach into MatrixContext for two
// fields when one is enough.
//
// Kept tiny on purpose — same rationale as `useSaved`. Aggregating
// here means the sidebar badge and the view header read from one
// derivation and can never disagree.

import { useMemo } from "react";

import { useMatrix } from "~/context/MatrixContext";
import { useRooms } from "~/hooks/useRooms";
import { sendReadReceipt, type UnreadGroup } from "~/lib/matrix";

export interface UseUnreadsResult {
  /** Groups, newest-room first. */
  groups: UnreadGroup[];
  /** Sum of `unreadCount` (= rendered events) across groups. The
   *  sidebar badge reads this directly so the digest header and
   *  sidebar stay in lockstep with what the view actually shows. */
  totalUnread: number;
  /** Sum of `highlightCount` across groups. */
  totalHighlight: number;
  /** Bulk fire-and-forget mark-all-read. */
  markAllRead: () => Promise<void>;
}

export function useUnreads(): UseUnreadsResult {
  const { client, unreadGroups } = useMatrix();
  const rooms = useRooms();

  const groups = useMemo(() => {
    const visibleRoomIds = new Set(rooms.map((room) => room.id));
    return unreadGroups.filter((group) => visibleRoomIds.has(group.roomId));
  }, [rooms, unreadGroups]);

  // Reduce in one pass — both totals are O(n) and the sidebar reads
  // them on every render via `useMatrix` -> this hook chain.
  const { totalUnread, totalHighlight } = useMemo(() => {
    let total = 0;
    let highlight = 0;
    for (const group of groups) {
      total += group.unreadCount;
      highlight += group.highlightCount;
    }
    return { totalUnread: total, totalHighlight: highlight };
  }, [groups]);

  const markAllRead = async () => {
    if (!client) return;
    await Promise.all(
      groups.map(async (group) => {
        const last = group.events[group.events.length - 1];
        if (!last) return;
        await sendReadReceipt(client, group.roomId, last.id);
      }),
    );
  };

  return {
    groups,
    totalUnread,
    totalHighlight,
    markAllRead,
  };
}
